<template>
  <Layout>
    <v-row justify="center" class="my-2">
      <v-col>
        <h3 class="title">
          AI ตรวจสอบรูปภาพ
          <div class="badge-beta">{{ VERSION.AI }}</div>
        </h3>
      </v-col>
    </v-row>

    <div class="container">
      <v-row
        class="ai-image-detection-container"
        align="center"
        justify="center"
        justify-sm="start"
      >
        <v-col cols="12" sm="6" md="3">
          <v-dialog
            ref="dialog"
            v-model="dateRangeModal"
            :return-value.sync="date"
            persistent
            width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="dateRangeText"
                v-on="on"
                v-bind="attrs"
                label="เลือกวันที่"
                prepend-icon="mdi-calendar"
                readonly
                hide-details
              ></v-text-field>
            </template>
            <v-date-picker v-model="dates" range>
              <v-spacer></v-spacer>
              <v-btn
                text
                color="primary"
                @click="dateRangeModal = false"
              >
                ยกเลิก
              </v-btn>
              <v-btn
                text
                color="primary"
                @click="$refs.dialog.save(date)"
              >
                ตกลง
              </v-btn>
            </v-date-picker>
          </v-dialog>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-select
            v-model="answer"
            :items="answerList"
            label="สถานะ"
            hide-details
          ></v-select>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-select
            v-model="selectBU"
            :items="bu"
            label="BU"
            item-text="bu_name"
            item-value="bu_id"
            multiple
            chips
            small-chips
            deletable-chips
            hide-details
          ></v-select>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-autocomplete
            v-model="selectStore"
            :items="storeDataFilterByBuId"
            label="สาขา"
            item-text="store_name"
            item-value="store_id"
            multiple
            chips
            small-chips
            deletable-chips
            hide-details
          >
            <template v-slot:selection="data">
              <v-chip
                :selected="data.selected"
                close
                small
                class="custom-chip"
                @click:close="removeChipSelectStore(data.item)"
              >
                <div class="text-wrap-1-line">
                  {{ data.item.store_name }}
                </div>
              </v-chip>
            </template>
          </v-autocomplete>
        </v-col>
        <v-col cols="12" sm="12" md="3">
          <v-autocomplete
            v-model="selectCategories"
            :items="categories"
            label="หมวดหมู่"
            multiple
            chips
            small-chips
            deletable-chips
            hide-details
          />
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-text-field
            v-model="searchSKU"
            label="โปรดักโค้ด"
            oninput="this.value=this.value.replace(/\s/g, '')"
            hide-details
            @keyup.enter="handleSearch"
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-text-field
            v-model="searchSerial"
            label="ซีเรียล"
            oninput="this.value=this.value.replace(/\s/g, '')"
            hide-details
            @keyup.enter="handleSearch"
          ></v-text-field>
        </v-col>
        <v-col cols="auto">
          <v-btn elevation="2" color="primary" @click="handleSearch">
            ค้นหา
          </v-btn>
        </v-col>
        <v-col cols="auto">
          <v-btn elevation="2" @click="reset">ล้าง</v-btn>
        </v-col>
      </v-row>
    </div>

    <template v-if="stockView">
      <v-row justify="center" no-gutters>
        <v-col cols="12">
          <CollectionAiImageDetection
            :data="data"
            :meta="meta"
            :isCanUpdate="stockEdit"
            :is-loading="loading"
            @update:page="handleChangePage"
            @submit="handleChangeStatus"
          />
        </v-col>
      </v-row>
    </template>
    <template v-else>
      <v-row justify="center" align="center">
        <v-alert type="error">{{ message }}</v-alert>
      </v-row>
    </template>
  </Layout>
</template>

<script>
import Layout from '@/components/Layout'
import CollectionAiImageDetection from '@/components/CollectionAiImageDetection.vue'
import { mapState } from 'vuex'
import moment from 'moment'
import VERSION from '@/enums/version'

export default {
  computed: {
    ...mapState({
      me: (state) => state.me.data,
      data: (state) => state.aiImageDetection.aiImageDetectionList,
      meta: (state) => state.aiImageDetection.aiImageDetectionMeta,
      loading: (state) => state.aiImageDetection.isLoading,
      message: (state) => state?.error?.message,
      submitError: (state) => state.aiImageDetection.submit.isError,

      // Filter
      buData: (state) => state.masterData.bu,
      storeData: (state) => state.masterData.store,
      categories: (state) => state.masterData.categories,
    }),
    dateRangeText() {
      return this.dates.join(` ~ `)
    },
    dates: {
      get() {
        return this.$store.state.aiImageDetection.selectDate
      },
      set(value) {
        const val = value.sort((a, b) => new Date(a) - new Date(b))
        this.$store.commit('aiImageDetection/SET_SELECT_DATE', val)
      },
    },
    bu() {
      return this?.buData?.filter((i) => i?.bu_id !== 'all')
    },
    storeDataFilterByBuId() {
      return this?.storeData
    },
    stockView() {
      return this.me?.permission?.stock_view
    },
    stockEdit() {
      return this.me?.permission?.stock_edit
    },
  },

  components: {
    CollectionAiImageDetection,
    Layout,
  },

  data() {
    return {
      VERSION,
      currentPage: 1,
      dateRangeModal: false,
      date: null,
      selectBU: [],
      selectStore: [],
      selectCategories: ['iPhone'],
      searchSKU: '',
      searchSerial: '',
      answer: false, // true | false | undefined
      answerList: [
        {
          text: 'ทั้งหมด',
          value: null,
        },
        {
          text: 'ปกติ',
          value: true,
        },
        {
          text: 'ผิดปกติ',
          value: false,
        },
      ],
    }
  },

  mounted() {
    this.fetchData()
  },

  methods: {
    handleParams() {
      const params = {
        page: this.currentPage || 1,
        // answer: false,
        ...(this.answer !== null && { answer: this.answer }), // true | false | undefined
        // for query string  'bu_ids[]'
        bu_ids: this.selectBU?.length > 0 ? this.selectBU : undefined,
        // for query string 'store_ids[]'
        store_ids:
          this.selectStore?.length > 0 ? this.selectStore : undefined,
        start: this.dates[0] || undefined,
        end: this.dates[1] || undefined,
        categories:
          this.selectCategories.length > 0
            ? this.selectCategories
            : undefined,
        sku: this.searchSKU || undefined,
        serial: this.searchSerial || undefined,
      }

      return params
    },
    async fetchData() {
      this.$store.dispatch('masterData/getBu')
      this.$store.dispatch('masterData/getStore')
      this.$store.dispatch('masterData/getCategories')
      await this.getData()
    },
    async getData() {
      const params = this.handleParams()
      await this.$store.dispatch(
        'aiImageDetection/getAiImageDetection',
        params,
      )
    },
    handleSearch() {
      this.currentPage = 1
      this.getData()
    },
    handleChangePage(page) {
      this.currentPage = page
      this.getData()
    },
    reset() {
      this.page = 1
      this.selectBU = []
      this.selectStore = []
      this.selectCategories = []
      this.searchSKU = ''
      this.searchSerial = ''
      this.$store.commit('aiImageDetection/SET_SELECT_DATE', [
        moment().format('YYYY-MM-DD'),
        moment().format('YYYY-MM-DD'),
      ])
      this.getData()
    },
    removeChipSelectStore(item) {
      const index = this.selectStore.findIndex(
        (i) => i === item.store_id,
      )
      if (index !== -1) {
        this.selectStore.splice(index, 1)
      }
    },
    async handleChangeStatus(form) {
      await this.$store.dispatch(
        'aiImageDetection/updateStatusAiImageDetection',
        form,
      )
      await this.getData()
    },
  },
}
</script>

<style lang="scss">
.title {
  .badge-beta {
    display: inline;
    margin-left: 4px;

    color: #ff4d4f;
    background: #fff2f0;
    border: 1px solid #ffccc7;
    padding: 0px 4px;
    border-radius: 4px;

    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.5px;

    vertical-align: middle;
  }
}

.text-wrap-1-line {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
